'use client';

import { useContext, useEffect, useState } from 'react';
import clsx from 'clsx';

import { languagesConfig, TLanguagesConfigItem } from '@northladder/utilities';

import { useHrefOptions } from '../../hooks';
import { LocaleContext } from '../../types';

import { LocaleMenu } from './LocaleMenu';
import { SetLocaleCTA } from './SetLocaleCTA';

interface ILocaleSwitcherProps {
  /**
   * The class to be passed down the the container parent element.
   */
  className?: string;
  /**
   * This determines whether the switch is `inline` with other components or
   * floating absolutely in the view.
   */
  isInline?: boolean;
  /**
   * localCTAClassName to configure the localCTA class.
   */
  localCTAClassName?: string;
}

/**
 * -----------------------------------------------------------------------------
 * This renders a floating menu dropdown (incase multi-languages are supported),
 * or nav link (for just duo language support) that's used in selecting the
 * preferred language by the user.
 */
export function LocaleSwitcher({
  className = '',
  isInline = false,
  localCTAClassName = '',
}: ILocaleSwitcherProps) {
  const { locale } = useHrefOptions();
  const { availableLanguages } = useContext(LocaleContext);

  const [selectableLanguages, setSelectableLanguages] = useState<
    TLanguagesConfigItem[]
  >([]);

  useEffect(() => {
    if (!availableLanguages?.length) {
      setSelectableLanguages(languagesConfig);
      return;
    }

    const filtered = languagesConfig.filter(
      (lang) => availableLanguages.includes(lang.id)
      // eslint-disable-next-line function-paren-newline
    );

    setSelectableLanguages(filtered);
  }, [locale, availableLanguages]);

  /**
   * For only 2 locales, will just toggle between, otherwise show a dropdown.
   */
  const isDuoLocale = selectableLanguages.length === 1;

  /**
   * If rendered inline besides other components, render it as as a smaller
   * pill/rounded, so it can fit in place.
   */
  const wrapperClass = isInline
    ? '!relative !right-auto !left-auto block 2xl:hidden'
    : 'hidden 2xl:block';

  /**
   * Reduce white space on the duo lang pill/button cta, as content is a bit
   * much less without the dropdown icon.
   */
  const duoLocaleClass = isDuoLocale ? 'w-20' : 'min-w-36 w-auto';

  return (
    <div
      className={clsx(
        'font-primary absolute end-20 z-[60]',
        wrapperClass,
        duoLocaleClass,
        className
      )}
    >
      {
        /**
         * Render a single nav link button to change the locale if only one extra
         * locale is present. Otherwise render a dropdown menu to choose from.
         */
        isDuoLocale ? (
          <SetLocaleCTA
            className={localCTAClassName}
            language={selectableLanguages[0]}
          />
        ) : (
          <LocaleMenu items={selectableLanguages} />
        )
      }
    </div>
  );
}
